// eslint-disable-next-line no-restricted-imports
import styled from '@emotion/styled';
import type { PopoverContextProps } from '@iheartjane/reefer';
import {
  Button,
  CheckIcon,
  ChevronDownIcon,
  Flex,
  List,
  Popover,
  TrashIcon,
  Typography,
} from '@iheartjane/reefer';
import range from 'lodash-es/range';
import { useCallback, useMemo } from 'react';

import { useKioskSpacing } from '@jane/kiosk/hooks';

import type { CartRowProduct } from './cartRowProduct';
import { maxQuantityForProduct } from './cartRowProduct';

interface Props<T extends CartRowProduct> {
  onChangeQuantity: (product: T, quantity: string) => void;
  onRemove: (productId: number) => void;
  product: T;
}

const StyledQtyButton = styled(Button)(() => {
  const { defaultSpacing, getKioskSpacing } = useKioskSpacing({
    defaultPadSize: 16,
  });

  return {
    border: 'none',
    borderRadius: getKioskSpacing(32, 12),
    paddingBottom: getKioskSpacing(16, 12),
    paddingLeft: defaultSpacing,
    paddingRight: defaultSpacing,
    paddingTop: getKioskSpacing(16, 12),
  };
});

export const CartRowActions = <T extends CartRowProduct>({
  onChangeQuantity,
  onRemove,
  product,
}: Props<T>) => {
  const { defaultSpacing, isIpad } = useKioskSpacing();

  const quantityOptions = useMemo(
    () =>
      range(1, maxQuantityForProduct(product) + 1).map((q) => ({
        label: q.toString(),
        value: q,
      })),
    [product]
  );

  const onRemoveProduct = useCallback(() => {
    onRemove(product.product_id);
  }, [onRemove, product.product_id]);

  const onUpdateProduct = useCallback(
    (qty: (typeof quantityOptions)[number], qtyProduct: T) => {
      onChangeQuantity(qtyProduct, qty.label);
    },
    [onChangeQuantity]
  );
  const { getKioskSpacing } = useKioskSpacing();

  return (
    <>
      <Popover
        alignment={{
          horizontal: 'right',
          vertical: 'bottom',
        }}
        disableMobileStyling
        label="Select quantity"
        ml={defaultSpacing}
        openOn={quantityOptions.length <= 1 ? 'disabled' : 'click'}
        target={
          <StyledQtyButton
            disabled={quantityOptions.length <= 1}
            endIcon={<ChevronDownIcon size="sm" />}
            label={
              <Typography variant="body-semibold">
                {product.count.toString()}
              </Typography>
            }
            size={isIpad ? 'small' : 'default'}
            data-testid={`${product.name}-quantity-btn`}
            variant="tertiary"
          />
        }
      >
        {({ closePopover }: PopoverContextProps) => (
          <Popover.Content padding={false}>
            <List label="quantity selector" py={16}>
              {quantityOptions.map((qty) => (
                <List.Item
                  ariaLabel={qty.label}
                  onClick={() => {
                    onUpdateProduct(qty, product);
                    closePopover();
                  }}
                  key={qty.label}
                  px={24}
                  py={12}
                >
                  <Flex
                    alignItems="center"
                    justifyContent="space-between"
                    flexDirection="row"
                  >
                    <Typography variant="body-semibold">{qty.label}</Typography>
                    {product.count === qty.value && <CheckIcon />}
                  </Flex>
                </List.Item>
              ))}
            </List>
          </Popover.Content>
        )}
      </Popover>
      <Button.Icon
        ariaLabel={`Remove ${product.name} from cart`}
        icon={<TrashIcon size={isIpad ? 'md' : 'lg'} />}
        ml={getKioskSpacing(12, 4)}
        onClick={onRemoveProduct}
        variant="minimal"
      />
    </>
  );
};
